import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { RecordConversation as RecordConversationModal } from '../../modals/recordConversation';
import type { AnnouncementPayloadT } from '../../types';

import { useAnnouncementName } from './callback';

export type Payload = {
  announcement: null | AnnouncementPayloadT;
  nextNode: null | string;
};

type RecordConversationNodeAttributes = GenericNodeAttributes<
  Payload,
  PhoneExtraData
>;
type RecordConversationNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: RecordConversationNodeAttributes['translatePayload'] = (
  node,
) => {
  const { nextNode } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNode || uuid(),
    },
  ];
};

const updateConnection: RecordConversationNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNode: connectionId };

  return { ...node, payload };
};

export const RecordConversationNodeAttributes: RecordConversationNodeAttributes =
  {
    iconName: 'rec',
    label: 'Record conversation',
    helpText:
      'All conversations after this action in the flow will be recorded. The recording will be stored under Conversations.',
    color: hexStringToNumber(flowNodeColor.blue),
    translatePayload,
    updateConnection,
    NodeContent: RecordConversationNode,
    ActionModal: RecordConversationModal,
  };

export function RecordConversationNode({
  node,
  context,
}: RecordConversationNodeProps): JSX.Element {
  const { extraData } = context;
  const { payload } = node;
  const announcement = useAnnouncementName(payload.announcement, extraData);

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="normal" text="Announcement" />
            <Text weight="semibold" text={announcement} />
          </TextEllipsis>
        ),
        [announcement],
      )}
    </TextContent>
  );
}
